/* eslint-disable react/no-unescaped-entities */
/**
 * The Me I See™ — Client-Facing Interactive 8-Module Experience.
 * Route: /coaching/me-i-see  (requires coaching_client JWT in localStorage)
 *
 * Ports the reference HTML prototype to the real backend:
 *  - Sidebar with locked/unlocked/complete module states
 *  - Sticky topbar with overall progress
 *  - Tabs: Lesson · Worksheet · Journal · Activity
 *  - AI feedback card (5-part structure, purple + green NEXT ACTION)
 *  - Certificate screen on module 8 completion
 *
 * Persistence is fully backend — every worksheet/journal/activity save
 * hits the API and updates local state from the response.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, Circle, LogOut, ChevronRight, Loader2, Award, Printer } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "me_i_see_token";
const EMAIL_KEY = "me_i_see_email";
const NAME_KEY = "me_i_see_name";

// Brand tokens (locked palette)
const COLOR = {
  purple: "#5B2C83",
  purpleDark: "#421e5e",
  purplePale: "#f1eaf6",
  green: "#7FA66A",
  silver: "#C0C0C0",
  silverPale: "#f6f4f0",
};

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

async function apiGet(path) {
  const r = await fetch(`${API}${path}`, { headers: authHeaders() });
  if (r.status === 401 || r.status === 403) throw new Error("auth");
  return r.json();
}
async function apiPatch(path, body) {
  const r = await fetch(`${API}${path}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`patch failed ${r.status}`);
  return r.json();
}
async function apiPost(path, body) {
  const r = await fetch(`${API}${path}`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`post failed ${r.status}`);
  return r.json();
}

export default function MeISeeApp() {
  const navigate = useNavigate();
  const [curriculum, setCurriculum] = useState(null);
  const [progress, setProgress] = useState(null);
  const [activeModuleN, setActiveModuleN] = useState(1);
  const [tab, setTab] = useState("lesson"); // lesson | worksheet | journal | activity
  const [saving, setSaving] = useState(false);
  const [submittingActivity, setSubmittingActivity] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [error, setError] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      navigate("/coaching/login");
      return;
    }
    (async () => {
      try {
        const [c, p] = await Promise.all([
          apiGet(`/api/coaching/me-i-see/curriculum`),
          apiGet(`/api/coaching/me-i-see/progress`),
        ]);
        setCurriculum(c);
        setProgress(p);
        setActiveModuleN(p.current_module || 1);
      } catch (e) {
        if (String(e.message).includes("auth")) navigate("/coaching/login");
        else setError("Could not load your program. Please refresh.");
      }
    })();
  }, [navigate]);

  const activeCurriculum = useMemo(
    () => curriculum?.modules.find((m) => m.number === activeModuleN),
    [curriculum, activeModuleN],
  );
  const activeProgress = useMemo(
    () => progress?.modules.find((m) => m.module_number === activeModuleN),
    [progress, activeModuleN],
  );

  function pickModule(n) {
    const mp = progress?.modules.find((x) => x.module_number === n);
    if (!mp || !mp.unlocked) return;
    setActiveModuleN(n);
    setTab("lesson");
  }

  async function reloadProgress() {
    const p = await apiGet(`/api/coaching/me-i-see/progress`);
    setProgress(p);
    return p;
  }

  function scheduleSave(fn) {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await fn();
        await reloadProgress();
      } catch { /* ignore transient */ }
      setSaving(false);
    }, 600);
  }

  function updateWorksheetLocal(idx, value) {
    setProgress((prev) => {
      const next = { ...prev, modules: prev.modules.map((m) => {
        if (m.module_number !== activeModuleN) return m;
        const answers = [...(m.worksheet_answers || ["", "", "", ""])];
        answers[idx] = value;
        return { ...m, worksheet_answers: answers };
      }) };
      const answers = next.modules.find((m) => m.module_number === activeModuleN).worksheet_answers;
      scheduleSave(() => apiPatch(`/api/coaching/me-i-see/module/${activeModuleN}/worksheet`, { answers }));
      return next;
    });
  }
  function updateJournalLocal(idx, value) {
    setProgress((prev) => {
      const next = { ...prev, modules: prev.modules.map((m) => {
        if (m.module_number !== activeModuleN) return m;
        const answers = [...(m.journal_answers || ["", "", "", "", ""])];
        answers[idx] = value;
        return { ...m, journal_answers: answers };
      }) };
      const answers = next.modules.find((m) => m.module_number === activeModuleN).journal_answers;
      scheduleSave(() => apiPatch(`/api/coaching/me-i-see/module/${activeModuleN}/journal`, { answers }));
      return next;
    });
  }
  function updateActivityLocal(value) {
    setProgress((prev) => ({ ...prev, modules: prev.modules.map((m) =>
      m.module_number === activeModuleN ? { ...m, activity_response: value } : m,
    ) }));
  }
  async function submitActivity() {
    if (!activeProgress) return;
    const text = (activeProgress.activity_response || "").trim();
    if (text.length < 20) return;
    setSubmittingActivity(true);
    try {
      await apiPost(`/api/coaching/me-i-see/module/${activeModuleN}/activity`, { response: text });
      const p = await reloadProgress();
      if (p.program_complete && activeModuleN === 8) setCertOpen(true);
    } catch (e) {
      setError("We couldn't submit your activity. Please try again.");
    }
    setSubmittingActivity(false);
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(NAME_KEY);
    navigate("/coaching/login");
  }

  if (!curriculum || !progress) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COLOR.silverPale }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: COLOR.purple }} />
      </div>
    );
  }

  return (
    <div data-testid="me-i-see-app" className="min-h-screen" style={{ background: COLOR.silverPale, fontFamily: "'Inter', sans-serif" }}>
      {/* Topbar */}
      <header className="sticky top-0 z-20 bg-white border-b" style={{ borderColor: "#e5e5e5" }}>
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: COLOR.purple, fontWeight: 600 }}>
              Program 01 · The Me I See™
            </div>
            <div className="text-sm text-slate-700" style={{ fontFamily: "'Playfair Display', serif" }}>
              Identity Awareness · 8 Weeks
            </div>
          </div>
          <div className="flex items-center gap-4 flex-1 max-w-md">
            <div className="flex-1">
              <div className="h-2 rounded-full overflow-hidden" style={{ background: COLOR.silverPale }}>
                <div
                  data-testid="progress-fill"
                  className="h-full transition-all duration-500"
                  style={{ width: `${progress.progress_pct}%`, background: COLOR.purple }}
                />
              </div>
              <div className="text-[11px] text-slate-500 mt-1" data-testid="progress-label">
                {progress.completed_count} / {progress.total_modules} Modules · {progress.progress_pct}%
              </div>
            </div>
            <button
              onClick={logout}
              className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              data-testid="me-i-see-logout"
            >
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </header>

      {certOpen ? (
        <CertificateScreen
          progress={progress}
          onClose={() => setCertOpen(false)}
        />
      ) : (
        <div className="max-w-6xl mx-auto px-6 py-8 grid md:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar */}
          <aside className="md:sticky md:top-24 self-start" data-testid="me-i-see-sidebar">
            <div className="rounded-2xl bg-white border p-3" style={{ borderColor: "#eee" }}>
              <div className="text-[10px] uppercase tracking-[0.18em] mb-2 px-2" style={{ color: COLOR.purple, fontWeight: 600 }}>
                Your 8-week journey
              </div>
              {progress.modules.map((m) => {
                const cur = curriculum.modules.find((c) => c.number === m.module_number);
                const active = m.module_number === activeModuleN;
                const clickable = m.unlocked;
                const done = m.completion_status;
                return (
                  <button
                    key={m.module_number}
                    onClick={() => pickModule(m.module_number)}
                    disabled={!clickable}
                    data-testid={`module-item-${m.module_number}`}
                    className="w-full text-left rounded-xl px-3 py-3 flex items-start gap-3 mb-1 transition"
                    style={{
                      background: active ? COLOR.purplePale : "transparent",
                      border: active ? `1px solid ${COLOR.purple}40` : "1px solid transparent",
                      opacity: clickable ? 1 : 0.55,
                      cursor: clickable ? "pointer" : "not-allowed",
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-medium"
                      style={{
                        background: done ? COLOR.green : (clickable ? COLOR.purplePale : COLOR.silverPale),
                        color: done ? "#fff" : (clickable ? COLOR.purple : "#999"),
                      }}
                    >
                      {done ? <CheckCircle2 className="w-4 h-4" /> : (clickable ? m.module_number : <Lock className="w-3.5 h-3.5" />)}
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Module {m.module_number}</div>
                      <div className="text-sm text-slate-900 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {cur?.title || `Week ${m.module_number}`}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Main panel */}
          <main className="min-w-0" data-testid="me-i-see-main">
            {activeCurriculum && activeProgress && (
              <>
                <div className="bg-white rounded-2xl border p-6 sm:p-8 mb-6" style={{ borderColor: "#eee" }}>
                  <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: COLOR.purple, fontWeight: 600 }}>
                    Module {activeCurriculum.number} of 8
                  </div>
                  <h1 className="mt-2 text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Playfair Display', serif", color: "#111" }}>
                    {activeCurriculum.title}
                  </h1>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    <span className="uppercase tracking-wider text-[10px] font-semibold" style={{ color: COLOR.purple }}>Objective · </span>
                    {activeCurriculum.objective}
                  </p>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b mb-6" style={{ borderColor: "#e5e5e5" }} data-testid="me-i-see-tabs">
                  {[
                    ["lesson", "Lesson", true],
                    ["worksheet", "Worksheet", _worksheetComplete(activeProgress.worksheet_answers)],
                    ["journal", "Journal", _journalComplete(activeProgress.journal_answers)],
                    ["activity", "Activity", _activityComplete(activeProgress.activity_response)],
                  ].map(([key, label, done]) => {
                    const active = tab === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setTab(key)}
                        data-testid={`tab-${key}`}
                        className="px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition"
                        style={{
                          borderColor: active ? COLOR.purple : "transparent",
                          color: active ? COLOR.purple : "#666",
                          fontWeight: active ? 600 : 400,
                        }}
                      >
                        {label}
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: done ? COLOR.green : COLOR.silver }}
                        />
                      </button>
                    );
                  })}
                </div>

                {tab === "lesson" && (
                  <div className="bg-white rounded-2xl border p-6 sm:p-8" style={{ borderColor: "#eee" }} data-testid="tab-panel-lesson">
                    <div
                      className="border-l-4 pl-5 py-2 mb-6 italic text-lg leading-relaxed"
                      style={{ borderColor: COLOR.purple, fontFamily: "'Playfair Display', serif", color: "#333" }}
                      data-testid="lesson-quote"
                    >
                      "{activeCurriculum.reflection_quote}"
                    </div>
                    <p className="text-slate-700 leading-relaxed" data-testid="lesson-body">
                      {activeCurriculum.coaching_body}
                    </p>
                  </div>
                )}

                {tab === "worksheet" && (
                  <div className="bg-white rounded-2xl border p-6 sm:p-8 space-y-5" style={{ borderColor: "#eee" }} data-testid="tab-panel-worksheet">
                    <div className="text-xs text-slate-500">
                      Answer each with a few sentences (minimum ~5 characters each).
                    </div>
                    {activeCurriculum.worksheet_prompts.map((q, i) => (
                      <div key={i}>
                        <label className="block text-sm font-medium text-slate-900 mb-2">
                          {i + 1}. {q}
                        </label>
                        <textarea
                          data-testid={`worksheet-input-${i}`}
                          value={(activeProgress.worksheet_answers || [])[i] || ""}
                          onChange={(e) => updateWorksheetLocal(i, e.target.value)}
                          className="w-full min-h-[92px] rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2"
                          style={{ borderColor: "#ddd", background: COLOR.silverPale }}
                          onFocus={(e) => e.target.style.borderColor = COLOR.purple}
                          onBlur={(e) => e.target.style.borderColor = "#ddd"}
                        />
                      </div>
                    ))}
                    <div className="text-xs text-slate-500">{saving ? "Saving…" : "Saved automatically as you write."}</div>
                  </div>
                )}

                {tab === "journal" && (
                  <div className="bg-white rounded-2xl border p-6 sm:p-8 space-y-5" style={{ borderColor: "#eee" }} data-testid="tab-panel-journal">
                    <div className="text-xs text-slate-500">
                      Five daily prompts. Answer each with at least a sentence (minimum ~10 characters).
                    </div>
                    {activeCurriculum.journal_prompts.map((q, i) => (
                      <div key={i}>
                        <label className="block text-sm font-medium text-slate-900 mb-2">
                          Day {i + 1} · {q}
                        </label>
                        <textarea
                          data-testid={`journal-input-${i}`}
                          value={(activeProgress.journal_answers || [])[i] || ""}
                          onChange={(e) => updateJournalLocal(i, e.target.value)}
                          className="w-full min-h-[92px] rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2"
                          style={{ borderColor: "#ddd", background: COLOR.silverPale }}
                          onFocus={(e) => e.target.style.borderColor = COLOR.purple}
                          onBlur={(e) => e.target.style.borderColor = "#ddd"}
                        />
                      </div>
                    ))}
                    <div className="text-xs text-slate-500">{saving ? "Saving…" : "Saved automatically as you write."}</div>
                  </div>
                )}

                {tab === "activity" && (
                  <div className="bg-white rounded-2xl border p-6 sm:p-8 space-y-5" style={{ borderColor: "#eee" }} data-testid="tab-panel-activity">
                    <div className="rounded-xl border-l-4 p-5" style={{ borderColor: COLOR.purple, background: COLOR.purplePale }}>
                      <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: COLOR.purple }}>This week's activity</div>
                      <p className="text-slate-800 leading-relaxed" data-testid="activity-prompt">{activeCurriculum.activity_prompt}</p>
                    </div>
                    <label className="block text-sm font-medium text-slate-900">Your response</label>
                    <textarea
                      data-testid="activity-input"
                      value={activeProgress.activity_response || ""}
                      onChange={(e) => updateActivityLocal(e.target.value)}
                      placeholder="What did you do? What came up for you?"
                      className="w-full min-h-[180px] rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2"
                      style={{ borderColor: "#ddd", background: COLOR.silverPale }}
                      onFocus={(e) => e.target.style.borderColor = COLOR.purple}
                      onBlur={(e) => e.target.style.borderColor = "#ddd"}
                    />
                    <div className="text-xs text-slate-500">
                      Minimum ~20 characters. When you submit, Dr. Christy will respond with a coaching reflection.
                    </div>
                    <button
                      onClick={submitActivity}
                      disabled={submittingActivity || (activeProgress.activity_response || "").trim().length < 20}
                      data-testid="submit-activity-btn"
                      className="rounded-full px-6 py-3 text-sm font-medium text-white transition disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                      style={{ background: COLOR.purple }}
                      onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.background = COLOR.purpleDark)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = COLOR.purple)}
                    >
                      {submittingActivity ? <><Loader2 className="w-4 h-4 animate-spin" /> Reflecting…</> : activeProgress.ai_feedback ? "Update my response" : "Submit for coaching feedback"}
                      <ChevronRight className="w-4 h-4" />
                    </button>

                    {activeProgress.ai_feedback && (
                      <FeedbackCard
                        feedback={activeProgress.coach_override_feedback || activeProgress.ai_feedback}
                        overridden={!!activeProgress.coach_override_feedback}
                        source={activeProgress.ai_feedback.source}
                      />
                    )}

                    {activeProgress.completion_status && activeModuleN < 8 && (
                      <div className="rounded-xl border p-4 flex items-center justify-between" style={{ borderColor: COLOR.green, background: "#f0f6ec" }}>
                        <div className="text-sm" style={{ color: COLOR.green }}>
                          <strong>Module complete.</strong> Module {activeModuleN + 1} is unlocked.
                        </div>
                        <button
                          onClick={() => { setActiveModuleN(activeModuleN + 1); setTab("lesson"); }}
                          data-testid="advance-next-module-btn"
                          className="rounded-full px-4 py-2 text-xs font-medium text-white"
                          style={{ background: COLOR.green }}
                        >
                          Continue to Module {activeModuleN + 1} <ChevronRight className="w-3 h-3 inline" />
                        </button>
                      </div>
                    )}
                    {activeProgress.completion_status && activeModuleN === 8 && (
                      <button
                        onClick={() => setCertOpen(true)}
                        data-testid="open-certificate-btn"
                        className="rounded-full px-5 py-3 text-sm font-medium text-white inline-flex items-center gap-2"
                        style={{ background: COLOR.green }}
                      >
                        <Award className="w-4 h-4" /> View my completion certificate
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
            {error && <div className="mt-4 text-sm text-red-700">{error}</div>}
          </main>
        </div>
      )}
    </div>
  );
}

function _worksheetComplete(a) { return Array.isArray(a) && a.length === 4 && a.every((x) => (x || "").trim().length >= 5); }
function _journalComplete(a)   { return Array.isArray(a) && a.length === 5 && a.every((x) => (x || "").trim().length >= 10); }
function _activityComplete(t)  { return typeof t === "string" && t.trim().length >= 20; }

function FeedbackCard({ feedback, overridden, source }) {
  const sections = [
    ["REFLECTION", feedback.reflection],
    ["OBSERVATION", feedback.observation],
    ["REFRAME", feedback.reframe],
    ["ENCOURAGEMENT", feedback.encouragement],
  ];
  return (
    <div className="rounded-2xl border p-6" data-testid="ai-feedback-card" style={{ borderColor: `${COLOR.purple}30`, background: "#fff" }}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ background: COLOR.purple, fontFamily: "'Playfair Display', serif" }}>CV</div>
        <div>
          <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>Dr. Christy Vonta</div>
          <div className="text-[11px] text-slate-500">
            {overridden ? "Personal note from your coach" : source === "ai" ? "Coaching reflection" : "From Dr. Christy's library"}
          </div>
        </div>
      </div>
      {sections.map(([label, body]) => (
        <div key={label} className="mb-4">
          <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: COLOR.purple }}>{label}</div>
          <div className="text-sm text-slate-800 leading-relaxed">{body}</div>
        </div>
      ))}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: COLOR.green }}>NEXT ACTION</div>
        <div className="text-sm text-slate-800 leading-relaxed">{feedback.next_action}</div>
      </div>
    </div>
  );
}

function CertificateScreen({ progress, onClose }) {
  const initial = localStorage.getItem(NAME_KEY) || "";
  const [name, setName] = useState(initial);
  const [confirmed, setConfirmed] = useState(false);

  async function confirmAndPrint() {
    if (!name.trim()) return;
    try {
      await apiPost(`/api/coaching/me-i-see/certificate/confirm-name`, { display_name: name.trim() });
      localStorage.setItem(NAME_KEY, name.trim());
      setConfirmed(true);
      setTimeout(() => window.print(), 400);
    } catch {
      window.print();
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="certificate-screen">
      <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-700 mb-6" data-testid="cert-back-btn">
        ← Back to program
      </button>
      <div
        className="rounded-3xl p-10 sm:p-14 text-center print:border-4"
        style={{ background: "#fff", border: `4px double ${COLOR.purple}`, fontFamily: "'Playfair Display', serif" }}
        data-testid="certificate-doc"
      >
        <div className="text-[11px] uppercase tracking-[0.4em]" style={{ color: COLOR.purple, fontWeight: 600 }}>
          Christy Vonta Coaching &amp; Consulting · KCIS Coaching Door
        </div>
        <div className="my-8">
          <div
            className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white text-xs font-semibold"
            style={{ background: COLOR.purple }}
          >
            <Award className="w-8 h-8" />
          </div>
        </div>
        <div className="text-sm uppercase tracking-widest text-slate-500 mb-2">Identity Awareness Completion Certificate</div>
        <div className="text-xs text-slate-500">This certifies that</div>
        <input
          data-testid="cert-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your full name"
          className="mt-4 text-3xl sm:text-4xl border-b-2 border-transparent focus:border-purple-900 focus:outline-none text-center py-2 bg-transparent min-w-[280px]"
          style={{ color: "#111", borderBottomColor: COLOR.silver }}
        />
        <div className="text-sm text-slate-700 mt-6">
          has completed all 8 modules of
        </div>
        <div className="text-2xl mt-2" style={{ color: COLOR.purple }}>The Me I See™</div>
        <div className="text-xs text-slate-500 mt-1">Program 01 · The Healing Trilogy · 8-week Identity Awareness Journey</div>

        <div className="mt-14 flex flex-col items-center">
          <div className="text-2xl italic" style={{ color: COLOR.purple, fontFamily: "'Playfair Display', serif" }}>Dr. Christy Vonta</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Founder · Christy Vonta Coaching &amp; Consulting</div>
        </div>
      </div>

      <div className="mt-6 flex justify-center gap-3 print:hidden">
        <button
          onClick={confirmAndPrint}
          disabled={!name.trim()}
          data-testid="cert-print-btn"
          className="rounded-full px-6 py-3 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-40"
          style={{ background: COLOR.purple }}
        >
          <Printer className="w-4 h-4" /> {confirmed ? "Print again" : "Confirm & download"}
        </button>
      </div>
    </div>
  );
}
